import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, Shield, Crown, Ban, UserCheck, Search, Activity, TrendingUp,
  ChevronLeft, ChevronRight, Eye, Trash2, ShieldOff, ArrowUp, ArrowDown,
  Sparkles, MessageSquare, ShoppingBag, BookOpen, Building2, Package, Loader2,
  Stethoscope, AlertTriangle, CheckCircle2, Wrench,
} from 'lucide-react';
import AdminMessages from '@/components/admin/AdminMessages';
import AdminAnalytics from '@/components/admin/AdminAnalytics';
import AdminPushBroadcast from '@/components/admin/AdminPushBroadcast';
import { API } from '@/config/api';

const STATUS_FILTERS = [
  { value: 'all', label: 'All users' },
  { value: 'premium', label: 'Premium' },
  { value: 'free', label: 'Free' },
  { value: 'banned', label: 'Banned' },
  { value: 'admin', label: 'Admins' },
];

const SUBSCRIPTION_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'premium_monthly', label: 'Premium — Monthly' },
  { value: 'premium_annual', label: 'Premium — Annual' },
  { value: 'lifetime', label: 'Lifetime' },
];

function StatCard({ icon: Icon, label, value, sub, color = 'text-primary', testId }) {
  return (
    <Card className="p-5 glass-card" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
          <p className="text-3xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <Icon className={`h-7 w-7 ${color}`} />
      </div>
    </Card>
  );
}

function UserBadges({ user }) {
  const status = user?.subscription_status || 'free';
  const role = user?.role || 'user';
  const isPremium = status !== 'free';
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {role === 'admin' && (
        <Badge variant="default" className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/20">
          <Shield className="h-3 w-3 mr-1" /> Admin
        </Badge>
      )}
      {isPremium ? (
        <Badge variant="default" className="bg-primary/20 text-primary hover:bg-primary/20">
          <Crown className="h-3 w-3 mr-1" />
          {status.replace('premium_', '').replace('_', ' ')}
        </Badge>
      ) : (
        <Badge variant="outline" className="text-muted-foreground">Free</Badge>
      )}
      {user?.is_banned && (
        <Badge variant="destructive">
          <Ban className="h-3 w-3 mr-1" /> Banned
        </Badge>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; }
}

export default function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Seed panel
  const [seedingKey, setSeedingKey] = useState(null);
  const [seedResults, setSeedResults] = useState({});

  // DB health
  const [dbHealth, setDbHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [fixingHealth, setFixingHealth] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { credentials: 'include' });
      if (res.status === 403) { navigate('/dashboard'); return; }
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit, sort, order,
        ...(search ? { search } : {}),
        ...(statusFilter !== 'all' ? { status_filter: statusFilter } : {}),
      });
      const res = await fetch(`${API}/admin/users?${params}`, { credentials: 'include' });
      if (res.status === 403) { navigate('/dashboard'); return; }
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [page, limit, sort, order, search, statusFilter, navigate]);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) setCurrentUser(await res.json());
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => { fetchMe(); fetchStats(); }, [fetchMe, fetchStats]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openDetail = async (userId) => {
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, { credentials: 'include' });
      if (res.ok) setDetail(await res.json());
      else toast.error('Failed to load user details');
    } catch (e) { toast.error('Network error'); } finally { setDetailLoading(false); }
  };

  const refreshAfterAction = () => { fetchUsers(); fetchStats(); };

  const updateSubscription = async (userId, status) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/subscription`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription_status: status }),
      });
      if (res.ok) {
        toast.success(`Subscription set to ${status.replace('_', ' ')}`);
        refreshAfterAction();
        if (detail?.user?.user_id === userId) openDetail(userId);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update subscription');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const toggleBan = async (user) => {
    if (user.user_id === currentUser?.user_id) {
      toast.error("You can't ban yourself");
      return;
    }
    const banning = !user.is_banned;
    if (banning && !window.confirm(`Ban ${user.email}? They'll be logged out and unable to sign in.`)) return;
    try {
      const res = await fetch(`${API}/admin/users/${user.user_id}/ban`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_banned: banning }),
      });
      if (res.ok) {
        toast.success(banning ? 'User banned' : 'User unbanned');
        refreshAfterAction();
        if (detail?.user?.user_id === user.user_id) openDetail(user.user_id);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update ban status');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const toggleRole = async (user) => {
    if (user.user_id === currentUser?.user_id) {
      toast.error("You can't change your own role");
      return;
    }
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    if (!window.confirm(`${newRole === 'admin' ? 'Promote' : 'Demote'} ${user.email} ${newRole === 'admin' ? 'to admin' : 'to regular user'}?`)) return;
    try {
      const res = await fetch(`${API}/admin/users/${user.user_id}/role`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        toast.success(`Role set to ${newRole}`);
        refreshAfterAction();
        if (detail?.user?.user_id === user.user_id) openDetail(user.user_id);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update role');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const deleteUser = async (user) => {
    if (user.user_id === currentUser?.user_id) {
      toast.error("You can't delete yourself");
      return;
    }
    if (!window.confirm(`PERMANENTLY DELETE ${user.email}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/users/${user.user_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        toast.success('User deleted');
        setDetailOpen(false);
        refreshAfterAction();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete user');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const handleSortClick = (field) => {
    if (sort === field) {
      setOrder(order === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(field); setOrder('desc');
    }
    setPage(1);
  };

  const SEED_ACTIONS = [
    { key: 'content-pack', label: 'Marketplace + Collections + Education + Suppliers', icon: Package,
      endpoint: '/seed/content-pack',
      description: 'Seeds 50 protocols, 6 collections, 15 articles, 8 suppliers (idempotent)' },
    { key: 'community', label: 'Community Forum Bots + Threads', icon: MessageSquare,
      endpoint: '/community/seed-content',
      description: 'Seeds 6 AI bots, 24 threads, 50 multi-bot replies (idempotent)' },
  ];

  const runSeed = async (action) => {
    setSeedingKey(action.key);
    try {
      const res = await fetch(`${API}${action.endpoint}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const partialErrors = Array.isArray(data.errors) ? data.errors : [];
        setSeedResults((prev) => ({
          ...prev,
          [action.key]: {
            ok: partialErrors.length === 0,
            data,
            errors: partialErrors,
            at: new Date().toLocaleTimeString(),
          },
        }));
        if (partialErrors.length === 0) {
          toast.success(`${action.label} seeded`);
        } else {
          toast.warning(`${action.label} partially seeded — ${partialErrors.length} warning(s)`);
        }
        fetchStats();
      } else {
        const detail = data.detail || data.message || `HTTP ${res.status} ${res.statusText}`;
        setSeedResults((prev) => ({ ...prev, [action.key]: { ok: false, error: detail } }));
        toast.error(detail);
      }
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
      setSeedResults((prev) => ({ ...prev, [action.key]: { ok: false, error: e.message } }));
    } finally {
      setSeedingKey(null);
    }
  };

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const res = await fetch(`${API}/admin/db-health`, { credentials: 'include' });
      if (res.ok) {
        setDbHealth(await res.json());
      } else {
        toast.error('Failed to run health check');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setHealthLoading(false);
    }
  };

  const runAutoFix = async () => {
    if (!window.confirm('Auto-fix will backfill empty/missing string fields with safe defaults across the entire DB. Continue?')) return;
    setFixingHealth(true);
    try {
      const res = await fetch(`${API}/admin/db-health/auto-fix`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Auto-fix complete: ${data.total_fixed} fields backfilled`);
        runHealthCheck(); // re-run to refresh
      } else {
        toast.error(data.detail || 'Auto-fix failed');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setFixingHealth(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sort !== field) return null;
    return order === 'desc' ? <ArrowDown className="h-3 w-3 ml-1 inline" /> : <ArrowUp className="h-3 w-3 ml-1 inline" />;
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" /> Admin Panel
          </h1>
          <p className="text-sm text-muted-foreground mt-1">User management, metrics, and platform oversight</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="admin-stats">
          <StatCard testId="stat-total-users" icon={Users} label="Total Users" value={stats.users.total}
            sub={`+${stats.users.new_signups_7d} this week`} />
          <StatCard testId="stat-premium-users" icon={Crown} label="Premium" value={stats.users.premium}
            sub={`${stats.users.total > 0 ? Math.round((stats.users.premium / stats.users.total) * 100) : 0}% conversion`} color="text-amber-400" />
          <StatCard testId="stat-active-24h" icon={Activity} label="Active (24h)" value={stats.users.active_24h}
            sub={`${stats.users.active_7d} in last 7 days`} color="text-emerald-400" />
          <StatCard testId="stat-banned-users" icon={Ban} label="Banned" value={stats.users.banned}
            sub={`${stats.users.admins} admin${stats.users.admins === 1 ? '' : 's'}`} color="text-destructive" />
        </div>
      )}

      {/* Platform stats secondary row */}
      {stats && (
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          <StatCard testId="stat-doses" icon={TrendingUp} label="Doses logged" value={stats.platform.total_doses_logged} color="text-blue-400" />
          <StatCard testId="stat-vials" icon={Activity} label="Vials tracked" value={stats.platform.total_vials} color="text-cyan-400" />
          <StatCard testId="stat-threads" icon={Users} label="Forum threads" value={stats.platform.total_forum_threads} color="text-purple-400" />
        </div>
      )}

      {/* SEED PANEL — admin can re-trigger content seeds without DevTools */}
      <Card className="p-5 glass-card" data-testid="admin-seed-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-bold">Content Seeding</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Use these on production after a fresh deploy. All seeds are idempotent — safe to re-run.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SEED_ACTIONS.map((action) => {
            const Icon = action.icon;
            const isRunning = seedingKey === action.key;
            const result = seedResults[action.key];
            return (
              <div
                key={action.key}
                className="p-4 rounded-lg border border-border bg-secondary/20"
                data-testid={`seed-action-${action.key}`}
              >
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4 text-primary flex-shrink-0" />
                      <p className="font-medium text-sm">{action.label}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{action.description}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="w-full"
                  variant="default"
                  disabled={isRunning}
                  onClick={() => runSeed(action)}
                  data-testid={`run-seed-${action.key}`}
                >
                  {isRunning ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Seeding…</>
                  ) : (
                    <><Sparkles className="h-3.5 w-3.5 mr-2" /> Run seed</>
                  )}
                </Button>
                {result && (
                  <div
                    className={`mt-2 text-[11px] ${result.ok ? 'text-emerald-400' : (result.errors?.length ? 'text-amber-400' : 'text-destructive')}`}
                    data-testid={`seed-result-${action.key}`}
                  >
                    {result.ok ? (
                      <p>✓ Done at {result.at} — {
                        Object.entries(result.data)
                          .filter(([k, v]) => typeof v === 'number')
                          .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                          .join(' • ')
                      }</p>
                    ) : result.errors && result.errors.length > 0 ? (
                      <details className="cursor-pointer">
                        <summary>⚠ Completed with {result.errors.length} warning(s) — click for details</summary>
                        <ul className="mt-1 list-disc ml-5 space-y-0.5">
                          {result.errors.map((err, i) => <li key={i} className="break-words">{err}</li>)}
                        </ul>
                      </details>
                    ) : (
                      <p>✗ {result.error}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* DB HEALTH PANEL */}      <Card className="p-5 glass-card" data-testid="admin-db-health-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-emerald-400" />
            <h2 className="text-lg font-bold">Database Health</h2>
            {dbHealth && (
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide ${
                  dbHealth.total_issues === 0
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                    : 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                }`}
                data-testid="health-status-badge"
              >
                {dbHealth.total_issues === 0 ? 'Healthy' : `${dbHealth.total_issues} issues`}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={runHealthCheck} disabled={healthLoading} data-testid="run-health-check">
              {healthLoading
                ? (<><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Checking…</>)
                : (<><Stethoscope className="h-3.5 w-3.5 mr-2" /> Run check</>)}
            </Button>
            {dbHealth && dbHealth.total_issues > 0 && (
              <Button size="sm" variant="default" onClick={runAutoFix} disabled={fixingHealth} data-testid="auto-fix-button"
                className="bg-amber-500 hover:bg-amber-600 text-black">
                {fixingHealth
                  ? (<><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Fixing…</>)
                  : (<><Wrench className="h-3.5 w-3.5 mr-2" /> Auto-fix safe fields</>)}
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Scans every critical collection for documents missing required fields — surfaces data drift before it causes 500 errors.
        </p>

        {!dbHealth && !healthLoading && (
          <p className="text-sm text-muted-foreground italic">Click <strong>Run check</strong> to scan your database.</p>
        )}

        {dbHealth && (
          <>
            {/* Collection summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-4" data-testid="health-collection-grid">
              {dbHealth.collections.map((c) => (
                <div
                  key={c.collection}
                  className={`p-2.5 rounded-lg border text-xs ${
                    c.health === 'ok' ? 'bg-emerald-500/5 border-emerald-500/30' :
                    c.health === 'warning' ? 'bg-amber-500/5 border-amber-500/30' :
                    'bg-destructive/5 border-destructive/30'
                  }`}
                  data-testid={`health-coll-${c.collection}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[10px] text-muted-foreground truncate">{c.collection}</span>
                    {c.health === 'ok'
                      ? <CheckCircle2 className="h-3 w-3 text-emerald-400 flex-shrink-0" />
                      : <AlertTriangle className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                  </div>
                  <p className="font-bold">{c.total_docs} <span className="text-[10px] text-muted-foreground font-normal">docs</span></p>
                  {c.issues_found > 0 && (
                    <p className="text-[10px] text-amber-400">{c.issues_found} issue{c.issues_found === 1 ? '' : 's'}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Detailed issues */}
            {dbHealth.issues.length === 0 ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                All {dbHealth.total_docs_scanned} documents have their critical fields. No drift detected.
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden" data-testid="health-issues-list">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left p-2.5 font-medium">Collection</th>
                      <th className="text-left p-2.5 font-medium">Field</th>
                      <th className="text-right p-2.5 font-medium">Missing</th>
                      <th className="text-left p-2.5 font-medium">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbHealth.issues.map((i, idx) => (
                      <tr key={idx} className="border-t border-border" data-testid={`health-issue-${idx}`}>
                        <td className="p-2.5 font-mono text-xs">{i.collection}</td>
                        <td className="p-2.5 font-mono text-xs text-amber-300">{i.field}</td>
                        <td className="p-2.5 text-right font-semibold">{i.missing_count}</td>
                        <td className="p-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${
                            i.severity === 'high'
                              ? 'bg-destructive/20 text-destructive'
                              : 'bg-amber-500/20 text-amber-300'
                          }`}>
                            {i.severity}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {/* USER ANALYTICS PANEL */}
      <AdminAnalytics />

      {/* PUSH NOTIFICATIONS BROADCAST */}
      <AdminPushBroadcast />

      {/* ADMIN MESSAGES INBOX */}
      <AdminMessages />

      {/* Filters */}
      <Card className="p-4 glass-card">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="admin-search"
              placeholder="Search by email, name, or user ID"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full md:w-48" data-testid="admin-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => (<SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Users table */}
      <Card className="glass-card overflow-hidden" data-testid="admin-users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left p-3 font-medium">
                  <button type="button" onClick={() => handleSortClick('email')} className="inline-flex items-center hover:text-foreground">
                    User<SortIcon field="email" />
                  </button>
                </th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Status</th>
                <th className="text-left p-3 font-medium hidden lg:table-cell">
                  <button type="button" onClick={() => handleSortClick('login_count')} className="inline-flex items-center hover:text-foreground">
                    Logins<SortIcon field="login_count" />
                  </button>
                </th>
                <th className="text-left p-3 font-medium hidden lg:table-cell">
                  <button type="button" onClick={() => handleSortClick('last_login')} className="inline-flex items-center hover:text-foreground">
                    Last login<SortIcon field="last_login" />
                  </button>
                </th>
                <th className="text-left p-3 font-medium hidden md:table-cell">
                  <button type="button" onClick={() => handleSortClick('created_at')} className="inline-flex items-center hover:text-foreground">
                    Joined<SortIcon field="created_at" />
                  </button>
                </th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground" data-testid="admin-users-loading">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No users match those filters.</td></tr>
              )}
              {!loading && users.map((u) => (
                <tr key={u.user_id} className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback>{(u.name || u.email || 'U').charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{u.name || '—'}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        {/* Mobile-only inline badges */}
                        <div className="md:hidden mt-1">
                          <UserBadges user={u} />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell"><UserBadges user={u} /></td>
                  <td className="p-3 hidden lg:table-cell text-muted-foreground">{u.login_count ?? 0}</td>
                  <td className="p-3 hidden lg:table-cell text-muted-foreground text-xs">{formatDate(u.last_login)}</td>
                  <td className="p-3 hidden md:table-cell text-muted-foreground text-xs">{formatDate(u.created_at)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                      {/* Quick premium toggle */}
                      {(u.subscription_status || 'free') === 'free' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10 h-7 px-2 text-xs"
                          onClick={() => updateSubscription(u.user_id, 'premium_annual')}
                          data-testid={`make-premium-${u.user_id}`}
                          title="Grant 1-year premium"
                        >
                          <Crown className="h-3.5 w-3.5 mr-1" /> Make Premium
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-border text-muted-foreground hover:bg-secondary h-7 px-2 text-xs"
                          onClick={() => updateSubscription(u.user_id, 'free')}
                          data-testid={`make-free-${u.user_id}`}
                          title="Revert to free tier"
                        >
                          Revert to Free
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openDetail(u.user_id)} data-testid={`view-user-${u.user_id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleBan(u)} data-testid={`ban-user-${u.user_id}`}
                        className={u.is_banned ? 'text-emerald-400' : 'text-destructive'}>
                        {u.is_banned ? <UserCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-3 border-t border-border text-sm">
          <p className="text-muted-foreground">
            Showing <strong>{users.length}</strong> of <strong>{total}</strong> users
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="admin-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* User Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle>User details</DialogTitle>
            <DialogDescription>Full profile, activity metrics, and admin actions.</DialogDescription>
          </DialogHeader>

          {detailLoading && <p className="text-center py-6 text-muted-foreground">Loading…</p>}

          {detail && (
            <div className="space-y-4">
              {/* Profile */}
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={detail.user.picture} />
                  <AvatarFallback className="text-lg">{(detail.user.name || detail.user.email || 'U').charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-lg truncate">{detail.user.name || '—'}</p>
                  <p className="text-sm text-muted-foreground truncate">{detail.user.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">ID: {detail.user.user_id}</p>
                </div>
                <UserBadges user={detail.user} />
              </div>

              {/* Metrics grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-xs text-muted-foreground">Total logins</p>
                  <p className="font-semibold">{detail.user.login_count ?? 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-xs text-muted-foreground">Active sessions</p>
                  <p className="font-semibold">{detail.activity.active_sessions}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-xs text-muted-foreground">Doses logged</p>
                  <p className="font-semibold">{detail.activity.doses_logged}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-xs text-muted-foreground">Vials tracked</p>
                  <p className="font-semibold">{detail.activity.vials_tracked}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-xs text-muted-foreground">Protocols</p>
                  <p className="font-semibold">{detail.activity.protocols_created}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <p className="text-xs text-muted-foreground">Forum posts</p>
                  <p className="font-semibold">{detail.activity.forum_posts}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Joined: <span className="text-foreground">{formatDate(detail.user.created_at)}</span></p>
                <p>Last login: <span className="text-foreground">{formatDate(detail.user.last_login)}</span></p>
                {detail.user.subscription_expires && (
                  <p>Subscription expires: <span className="text-foreground">{formatDate(detail.user.subscription_expires)}</span></p>
                )}
              </div>

              {/* Subscription change */}
              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium mb-2">Change subscription</p>
                <div className="flex flex-wrap gap-2" data-testid="subscription-actions">
                  {SUBSCRIPTION_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      size="sm"
                      variant={detail.user.subscription_status === opt.value ? 'default' : 'outline'}
                      onClick={() => updateSubscription(detail.user.user_id, opt.value)}
                      data-testid={`set-subscription-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Latest dose logs */}
              {detail.latest_doses && detail.latest_doses.length > 0 && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium mb-2">Recent dose logs</p>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {detail.latest_doses.map((d, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span>{d.peptide_name || '—'} · {d.dose_amount}{d.unit}</span>
                        <span>{formatDate(d.date)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {detail && (
              <>
                <Button variant="outline" onClick={() => toggleRole(detail.user)} data-testid="toggle-role-button"
                  disabled={detail.user.user_id === currentUser?.user_id}>
                  {detail.user.role === 'admin' ? (<><ShieldOff className="h-4 w-4 mr-2" /> Demote to user</>) : (<><Shield className="h-4 w-4 mr-2" /> Promote to admin</>)}
                </Button>
                <Button variant={detail.user.is_banned ? 'outline' : 'destructive'} onClick={() => toggleBan(detail.user)} data-testid="toggle-ban-button"
                  disabled={detail.user.user_id === currentUser?.user_id}>
                  {detail.user.is_banned ? (<><UserCheck className="h-4 w-4 mr-2" /> Unban</>) : (<><Ban className="h-4 w-4 mr-2" /> Ban user</>)}
                </Button>
                <Button variant="destructive" onClick={() => deleteUser(detail.user)} data-testid="delete-user-button"
                  disabled={detail.user.user_id === currentUser?.user_id}>
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
